export default function Grayson() {
  return (
    <section className="relative bg-[#1A1C1E] py-32 overflow-hidden">
      {/* Cyan circuitry patterns */}
      <div className="absolute inset-0 opacity-10">
        <svg className="w-full h-full" viewBox="0 0 1000 1000" preserveAspectRatio="none">
          <defs>
            <linearGradient id="circuitGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#00E5FF', stopOpacity: 0.6 }} />
              <stop offset="100%" style={{ stopColor: '#00E5FF', stopOpacity: 0.1 }} />
            </linearGradient>
          </defs>
          
          {/* Circuit board pattern */}
          <path d="M100 200 L300 200 L300 400 L500 400" stroke="url(#circuitGradient)" strokeWidth="2" fill="none" className="animate-pulse" />
          <path d="M900 300 L700 300 L700 500 L500 500" stroke="url(#circuitGradient)" strokeWidth="2" fill="none" className="animate-pulse" style={{ animationDelay: '0.5s' }} />
          <path d="M500 100 L500 300 L700 300" stroke="url(#circuitGradient)" strokeWidth="2" fill="none" className="animate-pulse" style={{ animationDelay: '1s' }} />
          <path d="M500 900 L500 700 L300 700" stroke="url(#circuitGradient)" strokeWidth="2" fill="none" className="animate-pulse" style={{ animationDelay: '1.5s' }} />
          
          {/* Circuit nodes */}
          <circle cx="300" cy="200" r="5" fill="#00E5FF" className="animate-pulse" />
          <circle cx="500" cy="400" r="5" fill="#00E5FF" className="animate-pulse" style={{ animationDelay: '0.5s' }} />
          <circle cx="700" cy="300" r="5" fill="#00E5FF" className="animate-pulse" style={{ animationDelay: '1s' }} />
          <circle cx="300" cy="700" r="5" fill="#00E5FF" className="animate-pulse" style={{ animationDelay: '1.5s' }} />
        </svg>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left side - Visual */}
          <div className="relative flex items-center justify-center">
            <div className="relative w-full max-w-md aspect-square">
              {/* Glowing background circle */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div 
                  className="w-80 h-80 rounded-full opacity-20 animate-pulse"
                  style={{
                    background: 'radial-gradient(circle, rgba(0, 229, 255, 0.4) 0%, rgba(0, 229, 255, 0) 70%)'
                  }}
                ></div>
              </div>

              {/* AI Avatar */}
              <div className="absolute inset-0 flex items-center justify-center">
                <img 
                  src="https://public.readdy.ai/ai/img_res/254a1b1b764277359372d2bdcc6e765d.jpg" 
                  alt="Grayson AI Orchestrator" 
                  className="w-80 h-80 object-cover rounded-full"
                  style={{
                    filter: 'drop-shadow(0 0 40px rgba(0, 229, 255, 0.4))'
                  }}
                />
              </div>

              {/* Orbiting elements */}
              <div className="absolute inset-0 flex items-center justify-center animate-spin" style={{ animationDuration: '20s' }}>
                <div className="relative w-96 h-96">
                  <div className="absolute top-0 left-1/2 w-3 h-3 bg-[#00E5FF] rounded-full -translate-x-1/2"></div>
                  <div className="absolute bottom-0 left-1/2 w-3 h-3 bg-[#00E5FF] rounded-full -translate-x-1/2"></div>
                  <div className="absolute left-0 top-1/2 w-3 h-3 bg-[#00E5FF] rounded-full -translate-y-1/2"></div>
                  <div className="absolute right-0 top-1/2 w-3 h-3 bg-[#00E5FF] rounded-full -translate-y-1/2"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <h2 className="text-5xl lg:text-6xl font-bold text-[#F4F7F6]" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Meet Grayson
              </h2>
              
              <h3 className="text-2xl text-[#00E5FF] font-semibold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Lead Systems Orchestrator
              </h3>
              
              <p className="text-lg text-[#7F8C8D] leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                Grayson monitors the automation infrastructure behind Graystone AI systems.
              </p>
              
              <p className="text-lg text-[#7F8C8D] leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                He analyzes patterns, detects anomalies, and ensures workflows remain stable.
              </p>
              
              <p className="text-lg text-[#7F8C8D] leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                Grayson handles the digital heavy lifting so your team can focus on strategic growth.
              </p>
            </div>

            {/* System Console */}
            <div className="relative bg-[#0D0E0F] border border-[#00E5FF]/30 rounded-lg p-6 mt-8">
              {/* Console header */}
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-[#7F8C8D]/20">
                <div className="w-3 h-3 rounded-full bg-[#00E5FF]"></div>
                <div className="w-3 h-3 rounded-full bg-[#7F8C8D]"></div>
                <div className="w-3 h-3 rounded-full bg-[#7F8C8D]"></div>
              </div>

              {/* Console content */}
              <div className="space-y-3" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                <p className="text-[#00E5FF] text-sm font-semibold">Weekly systems report</p>
                
                <div className="space-y-2 text-sm">
                  <p className="text-[#F4F7F6]">
                    <span className="text-[#7F8C8D]">Workflows active:</span> 42
                  </p>
                  <p className="text-[#F4F7F6]">
                    <span className="text-[#7F8C8D]">Tasks completed:</span> 1,286
                  </p>
                  <p className="text-[#F4F7F6]">
                    <span className="text-[#7F8C8D]">Anomalies flagged:</span> 0
                  </p>
                  <p className="text-[#F4F7F6]">
                    <span className="text-[#7F8C8D]">Pending approvals:</span> 2
                  </p>
                </div>

                <div className="pt-4 mt-4 border-t border-[#7F8C8D]/20">
                  <p className="text-[#00E5FF] text-sm italic">
                    Systems are nominal and workflows are fluid.
                  </p>
                  <p className="text-[#7F8C8D] text-sm mt-2">
                    — Grayson
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}